/**
 * GraphQL queries and mutations for content references
 * @module content-detail/content-references/queries
 */
import { gql } from '@apollo/client'

/**
 * Search for content to use as references
 * Used by the autocomplete field to find admin categories (or other content types)
 * Uses ilike for case-insensitive search (matches YI pattern)
 */
export const SEARCH_CONTENT = gql`
  query SearchContent(
    $contentType: String!
    $bundle: String
    $title: String
    $filterType: String
    $limit: Int = 50
    $language: [String] = ["en", "_primary"]
  ) {
    contentList(
      where: {
        contentType: { eq: $contentType }
        bundle: { eq: $bundle }
        title: { ilike: $title }
        filterType: { eq: $filterType }
      }
      language: $language
      limit: $limit
      order: [["title", "ASC"]]
    ) {
      content {
        id
        contentId
        contentType
        title
        published
        titleGroupId
      }
    }
  }
`

/**
 * Search for video content to use as references
 * Used by the video autocomplete field to find video content with images
 * Uses ilike for case-insensitive search
 */
export const SEARCH_VIDEO_CONTENT = gql`
  query SearchVideoContent(
    $title: String
    $limit: Int = 50
    $language: [String] = ["en", "_primary"]
  ) {
    contentList(
      where: { contentType: { eq: "video" }, title: { ilike: $title } }
      language: $language
      limit: $limit
      order: [["title", "ASC"]]
    ) {
      content {
        id
        contentId
        contentType
        title
        published
        titleGroupId
        imageContentRelationships(language: $language, types: ["d6-keyart-no-text"]) {
          id
          type
          image {
            id
            url
          }
        }
      }
    }
  }
`

/**
 * Create a single content reference
 * Used when adding individual content references
 */
export const CREATE_CONTENT_REFERENCE = gql`
  mutation CreateContentReference($contentReference: ContentReferenceCreateInput!) {
    contentReferenceCreate(contentReference: $contentReference) {
      contentReference {
        id
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
        contentReferenceTypesId
      }
    }
  }
`

/**
 * Create multiple content references in a batch
 * Used when saving new content reference selections
 */
export const CREATE_CONTENT_REFERENCES = gql`
  mutation CreateContentReferences($contentReferences: [ContentReferenceCreateInput!]!) {
    contentReferenceCreateMany(contentReferences: $contentReferences) {
      contentReferences {
        id
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
        contentReferenceTypesId
      }
    }
  }
`

/**
 * Delete a single content reference
 * Used when removing a content reference selection
 */
export const DELETE_CONTENT_REFERENCE = gql`
  mutation DeleteContentReference($contentReference: ContentReferenceDeleteInput!) {
    contentReferenceDelete(contentReference: $contentReference) {
      contentReference {
        id
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
      }
    }
  }
`
